export default {
  history: {
    columns: {
      currentStep: "Current step",
      fileName: "IFC file",
    },
    description: "IFC upload and pipeline jobs for this project",
    title: "IFC pipeline history",
  },
  pageTitle: "Execution",
  processing: {
    description: "Raytracing and processing jobs triggered by the pipeline",
    detailsModal: {
      title: "Job details",
    },
    title: "Processing jobs",
  },
  run: {
    acceptedFormat: "Accepted file format: {{formats}}",
    acceptedSize: "Max size: {{max}} MB",
    alreadyRunning: "A file is already being uploaded. Please wait for it to complete or cancel it to start a new one.",
    button: "Upload IFC & start pipeline",
    description: "Upload an IFC file to start the digital twin pipeline for this project",
    error: "Failed to start execution",
    executeRaytracingQuestion: "Run raytracing after IFC processing?",
    executeRaytracingTitle: "Execute raytracing",
    fileInputLabel: "Choose execution input file",
    fileSizeInvalid: "File size is invalid",
    fileTooLarge: "File is too large. Maximum size is {{max}} MB.",
    meshingMethodLabel: "Meshing method",
    noFileChosen: "No file chosen",
    selectFileFirst: "Please choose a file first",
    sensorOffsetLabel: "Sensor offset (m)",
    success: "Submitted successfully",
    title: "Run IFC pipeline",
    uploadAndRun: "Upload file & run Raytracing",
    uploadProgress: "{{value}}% uploaded",
  },
  schedules: {
    clickCellsHint: "Click on 2 cells to edit manually.",
    days: {
      fri: "Fri",
      holiday: "Hol",
      mon: "Mon",
      sat: "Sat",
      sun: "Sun",
      thu: "Thu",
      tue: "Tue",
      wed: "Wed",
    },
    detailsTitle: "Schedule Details",
    dimensions: {
      navigation: "Navigation",
      scenario: "Scenario",
      section: "Section",
      type: "Type",
    },
    duplicateSection: "Duplicate section",
    export: {
      button: "Copy Settings",
      copyFrom: "Copy From (Source)",
      copyTo: "Copy To (Destination)",
      description: "Replicate configurations across different scenarios, categories and room profiles.",
      footerInfo: "Existing target configurations on selected fields will be overwritten.",
      scenariosTitle: "Scenarios",
      sectionsTitle: "Room types",
      title: "Copy Scenario Settings",
      typesTitle: "Categories",
    },
    import: {
      button: "Template",
    },
    metrics: {
      appliances: "Appliances",
      illumination: "Illumination",
      occupancy: "Occupancy",
      ventilation: "Ventilation",
    },
    modal: {
      apply: "Apply",
      description: "Set a value for {{count}} selected cells.",
      title: "Set schedule value",
      valueLabel: "Value",
    },
    quickSetup: {
      activeValue: "Active Value",
      applyScenarios: "Apply to Scenarios",
      applySections: "Apply to Sections",
      applySetup: "Apply Setup",
      applyTypes: "Apply to Types",
      button: "Quick Setup",
      from: "From",
      inactiveValue: "Inactive Value",
      placeholder: "Quick setup content will be added here.",
      title: "Quick Setup",
      to: "To",
      workingDays: "Working Days",
      workingHours: "Working Hours",
    },
    scenarios: {
      base_case: "Base Case",
      high_efficiency: "High Efficiency",
      worst_case: "Worst Case",
    },
    sections: {
      canteen: "Canteen",
      hallway: "Hallway",
      lobby: "Lobby",
      meeting_room: "Meeting Room",
      office: "Office",
      storage: "Storage",
      technical_room: "Technical room",
      wc: "WC",
    },
    selectAll: "Select All",
    selectDimensions: {
      description: "Choose one option from each dimension to view its corresponding data table.",
      title: "Select Data Dimensions",
    },
    table: {
      hour: "Hour",
    },
    targetSelection: {
      scenarios: "Apply to Scenarios",
      sections: "Apply to Sections",
      types: "Apply to Types",
    },
  },
  steps: {
    description: "Current processing status and step-by-step progress",
    progress: "{{value}}% complete",
    scheduleProgress: {
      count: "{{inserted}} / {{total}}",
      entities: {
        pv_panels: "PV panels",
        rooms: "Rooms",
        walls: "Walls",
        windows: "Windows",
      },
      percent: "{{value}}%",
      phase: {
        done: "Done",
        extracting: "Extracting schedule",
        posting: "Posting schedule data",
        posting_pv_panels: "Posting PV panels",
        posting_rooms: "Posting rooms",
        posting_walls: "Posting walls",
        posting_windows: "Posting windows",
      },
      phaseLabel: "Current phase",
      title: "Schedule extraction progress",
    },
    title: "IFC File Processing",
  },
  tabs: {
    ifc: "IFC",
    transient_response: "Transient Response",
  },
  transient: {
    actions: {
      save: "Save config",
      saveSuccess: "Configuration saved.",
      trigger: "Trigger job",
      triggerFailed: "Failed to submit the job.",
      triggerSuccess: "Job submitted.",
    },
    configuration: {
      dataYear: "Data year",
      dayNightFlushing: {
        enableDayFlushing: "Enable day flushing",
        enableNightFlushing: "Enable night flushing",
        flushingDayHours: "Day flushing hours",
        flushingNightHours: "Night flushing hours",
        flushingTExhaustDayThreshold: "Day exhaust temperature threshold",
        flushingTExhaustNightThreshold: "Night exhaust temperature threshold",
        flushTempLowerDayLimit: "Lower day temperature limit",
        flushTempLowerNightLimit: "Lower night temperature limit",
        flushTempUpperDayLimit: "Upper day temperature limit",
        flushTempUpperNightLimit: "Upper night temperature limit",
      },
      deltaTimeI: "Delta time (min)",
      description: "Set simulation parameters for the transient response calculation.",
      dynamicHvacStartPoint: "Dynamic HVAC start point",
      heatRecoveryEff: "Heat recovery efficiency",
      measuredWeatherData: "Measured weather data",
      percentileToConsider: "Percentile to consider",
      recalculationMethod: {
        label: "Recalculation method",
        overwrite: "Overwrite",
        skip: "Skip",
      },
      requiredTMax: "Required T max",
      requiredTMin: "Required T min",
      roomFunctionVariant: {
        din_function: "DIN Function",
        label: "Room function variant",
        template_function: "Template Function",
      },
      scenariosToRun: "Scenarios to run",
      selectAtLeastOne: "Select at least one scenario.",
      tAirExhaust: "Exhaust air temperature",
      timeIntervalInMin: "Time interval (min)",
    },
    jobDetails: {
      configEmpty: "No configuration found for this job.",
      configFailed: "Failed to load configuration.",
      downloadLog: "Download log",
      downloadModal: {
        action: {
          cancel: "Cancel",
          close: "Close",
        },
        done: "Done",
        failed: "Download failed. Please try again.",
        failedZip: "Failed to create the zip file.",
        progress: "{{done}} / {{total}}",
        title: "Downloading files",
        zipLabel: "Creating zip",
      },
      downloadSelected: "Download selected",
      fileCount: "({{count}} files)",
      inputFiles: "Input files",
      inputsEmpty: "No input files found for this job.",
      inputsFailed: "Failed to load inputs.",
      iteration: "Iteration {{count}}",
      iterations: "Iterations",
      logFile: "Log file",
      logsEmpty: "No logs yet.",
      logsFailed: "Failed to load logs.",
      noMatchingFiles: "No files match your search.",
      noMatchingRooms: "No rooms match your search.",
      otherFiles: "Individual files",
      resultKind: "Result kind",
      resultsEmpty: "No result files found for this project.",
      resultsFailed: "Failed to load results.",
      roomCount: "({{count}} rooms finished)",
      roomDictionary: "Dictionaries",
      roomResults: "Hourly",
      scenarios: "Scenarios",
      searchFiles: "Search files",
      searchRooms: "Search rooms",
      tabs: {
        config: "Configuration",
        input: "Inputs",
        logs: "Logs",
        result: "Result",
        state: "State",
      },
      title: "Job details",
    },
    newRequest: "New Request",
    placeholders: {
      configuration: "Configuration options will be added here.",
      scenario: "Scenario definition options will be added here.",
    },
    requestHistory: {
      columns: {
        name: "Request",
        status: "Status",
      },
      empty: "No transient response jobs yet.",
      status: {
        completed: "Completed",
        pending: "Pending",
      },
      title: "Request history",
    },
    scenario: {
      addButton: "Add",
      addScenario: "Add scenario",
      cdirThreshold: "Cdir threshold",
      dayNightFlushing: {
        activeHours: "Active hours",
        bypass: "Bypass",
        day: "Day",
        enable: "Day / night flushing",
        enableRow: "Enable",
        exhaustTempThreshold: "Exhaust temp. threshold",
        lowerTempLimit: "Lower temp. limit",
        night: "Night",
        title: "Flushing Settings",
        upperTempLimit: "Upper temp. limit",
      },
      description: "Customize each scenario with simulation inputs.",
      detailedSimulation: "Detailed simulation",
      dirRadThreshold: "Dir-rad threshold",
      emptyState: "No scenarios found. Add scenarios in Technical Data first.",
      measuredWeatherData: "Measured weather data",
      reactiveParameter: {
        label: "Reactive parameter",
        load: "Load",
        room_air_temp: "Room air temperature",
      },
      removeScenario: "Remove scenario",
      selectScenario: "Select scenario",
      sfThreshold: "SF threshold",
      tryFile: "TRY file",
      tSupplyDynamic: "T supply dynamic",
      weatherDataYear: "Weather data year",
    },
    steps: {
      configuration: "Configuration",
      scenario: "Scenarios",
      schedules: "Schedules",
    },
  },
};
